import type {
  FormDebugEntry,
  FormInspectorListener,
} from "./form-inspector-store.types";

export type {
  FormDebugEntry,
  FormFieldError,
} from "./form-inspector-store.types";

const registry = new Map<string, FormDebugEntry>();
const listeners = new Set<FormInspectorListener>();
let snapshot: FormDebugEntry[] = [];
const sensitivePattern = /password|secret|token|cookie|price|bid|commercial/i;

function safeName(value: string): string {
  if (sensitivePattern.test(value)) return "[redacted]";
  return value
    .replace(/[\u0000-\u001f\u007f]/g, " ")
    .trim()
    .slice(0, 80);
}

function notify(): void {
  snapshot = Array.from(registry.values());
  listeners.forEach((l) => l());
}

export function subscribeFormInspector(
  listener: FormInspectorListener,
): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const SERVER_SNAPSHOT: FormDebugEntry[] = [];

export function getFormEntries(): FormDebugEntry[] {
  return snapshot;
}

export function getServerSnapshotFormEntries(): FormDebugEntry[] {
  return SERVER_SNAPSHOT;
}

export function clearFormRegistry(): void {
  registry.clear();
  notify();
}

export function registerForm(id: string, name: string): void {
  const safeId = safeName(id);
  registry.set(safeId, {
    id: safeId,
    name: safeName(name),
    isDirty: false,
    isValid: true,
    isSubmitting: false,
    isSubmitted: false,
    submitCount: 0,
    fieldCount: 0,
    errors: {},
    lastUpdated: Date.now(),
  });
  notify();
}

export function unregisterForm(id: string): void {
  registry.delete(safeName(id));
  notify();
}

export function updateFormEntry(
  id: string,
  update: Partial<Omit<FormDebugEntry, "id" | "name">>,
): void {
  const safeId = safeName(id);
  const existing = registry.get(safeId);
  if (!existing) return;
  const safeErrors = Object.fromEntries(
    Object.entries(update.errors ?? {}).map(([field, error]) => [
      safeName(field),
      { type: error.type ? safeName(error.type) : undefined },
    ]),
  );
  registry.set(safeId, {
    ...existing,
    ...update,
    errors: safeErrors,
    lastUpdated: Date.now(),
  });
  notify();
}
